import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PenTool, QrCode, Upload } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { api, get, post } from '../lib/api.ts'
import { fmtDateTime } from '../lib/format.ts'
import { Button, Dialog, ErrorNote, Field, Input, Sheet } from './ui.tsx'
import { t } from '../lib/i18n.ts'

interface Sigs { signatures: { id: string; name: string; role: string; signedAt: string; mine: boolean }[]; verificationCode: string | null; verificationUrl: string | null }

export function SignaturesPanel({ condoId, docId, canUpload, isPdf }: { condoId: string; docId: string; canUpload: boolean; isPdf: boolean }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['sigs', condoId, docId], queryFn: () => get<Sigs>(`/api/condos/${condoId}/documents/${docId}/signatures`), enabled: Boolean(condoId) })
  const [step, setStep] = useState<'idle' | 'code'>('idle')
  const [code, setCode] = useState('')
  const [upOpen, setUpOpen] = useState(false)
  const request = useMutation({ mutationFn: () => post(`/api/condos/${condoId}/documents/${docId}/sign/request`), onSuccess: () => setStep('code') })
  const sign = useMutation({
    mutationFn: () => post(`/api/condos/${condoId}/documents/${docId}/sign`, { code }),
    onSuccess: () => { toast.success(t('Documento assinado')); setStep('idle'); setCode(''); qc.invalidateQueries({ queryKey: ['sigs', condoId, docId] }) },
  })
  if (!q.data) return null
  const signed = q.data.signatures.some((s) => s.mine)
  return (
    <section className="mt-8">
      <h2 className="mb-2 text-xl font-bold">{t('Assinaturas')}</h2>
      <Sheet className="p-4">
        {q.data.signatures.length === 0 ? <p className="text-tinta-2">{t('Ninguém assinou esta versão ainda.')}</p> : (
          <ul className="mb-3">
            {q.data.signatures.map((s) => (
              <li key={s.id} className="flex flex-wrap items-baseline gap-x-2 border-b border-pauta py-2 last:border-b-0">
                <PenTool className="size-4 text-caneta" /><span className="font-semibold">{s.name}</span><span className="text-sm text-tinta-2">{s.role}, {fmtDateTime(s.signedAt)}</span>
              </li>
            ))}
          </ul>
        )}
        {!signed && step === 'idle' && <Button onClick={() => request.mutate()} busy={request.isPending}><PenTool className="size-5" />{' '}{t('Assinar com código do WhatsApp')}</Button>}
        {step === 'code' && (
          <form className="mt-2 flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); sign.mutate() }}>
            <Field label={t('Código recebido no WhatsApp')} htmlFor="sig-c"><Input id="sig-c" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className="w-40 text-center tracking-[0.3em]" autoFocus /></Field>
            <Button type="submit" busy={sign.isPending} disabled={code.length !== 6}>{t('Confirmar assinatura')}</Button>
          </form>
        )}
        <ErrorNote error={request.error ?? sign.error} />
        <p className="mt-3 text-sm text-tinta-2">{t('Assinatura eletrônica simples (Lei 14.063/2020): registramos quem assinou, quando, de onde e a impressão digital (SHA-256) exata do arquivo. Uma nova versão do documento exige novas assinaturas.')}</p>
        {q.data.verificationUrl && (
          <p className="mt-3 flex items-center gap-2 text-[0.95rem]"><QrCode className="size-4" />{' '}{t('Verificação pública:')}{' '}<a href={q.data.verificationUrl} className="break-all font-semibold text-caneta underline">{q.data.verificationUrl.replace(/^https?:\/\//, '')}</a></p>
        )}
        {canUpload && isPdf && <div className="mt-4"><Button variant="outline" onClick={() => setUpOpen(true)}><Upload className="size-5" />{' '}{t('Anexar via assinada no gov.br')}</Button></div>}
      </Sheet>
      {upOpen && <SignedCopyDialog condoId={condoId} docId={docId} onClose={() => setUpOpen(false)} />}
    </section>
  )
}

function SignedCopyDialog({ condoId, docId, onClose }: { condoId: string; docId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const m = useMutation({
    mutationFn: () => { const fd = new FormData(); fd.set('file', file!); return api<{ signature: string; prefixMatch: boolean }>('POST', `/api/condos/${condoId}/documents/${docId}/signed-copy`, fd) },
    onSuccess: (r) => {
      toast.success(r.signature === 'present' ? (r.prefixMatch ? t('Via assinada anexada. O conteúdo original está preservado.') : t('Via assinada anexada. Atenção: o arquivo foi regravado pelo assinador; confira no validador do ITI.')) : t('Arquivo anexado, mas não encontramos assinatura digital nele.'))
      qc.invalidateQueries({ queryKey: ['doc', condoId, docId] }); qc.invalidateQueries({ queryKey: ['sigs', condoId, docId] }); onClose()
    },
  })
  return (
    <Dialog open onClose={onClose} title={t('Anexar via assinada')}>
      <ol className="mb-4 list-decimal space-y-1 pl-5 text-[0.95rem]">
        <li>{t('Baixe o PDF deste documento.')}</li>
        <li>{t('Assine em')}{' '}<strong>{t('assinador.iti.br')}</strong>{' '}{t('com a sua conta gov.br (nível prata ou ouro), ou com certificado ICP-Brasil.')}</li>
        <li>{t('Envie aqui o PDF assinado. Ele vira a versão atual; a validade da assinatura pode ser conferida em')}{' '}<strong>{t('validar.iti.gov.br')}</strong>.</li>
      </ol>
      <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); if (file) m.mutate() }}>
        <input type="file" accept=".pdf,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} aria-label={t('PDF assinado')} />
        <ErrorNote error={m.error} />
        <div className="flex justify-end gap-2"><Button type="button" variant="quiet" onClick={onClose}>{t('Cancelar')}</Button><Button type="submit" disabled={!file} busy={m.isPending}>{t('Anexar')}</Button></div>
      </form>
    </Dialog>
  )
}
